














































































































































































import Vue from 'vue';
import store from '@/store';
import {
  OrgPeerGroup,
  PeerGroupMember,
  UNRULR_MANAGER_ID
} from '@/models/orgStructure';
import { MemberRole, WebRequestStatus, webRequestUtils } from '@/models';
import * as Utils from '@/utils';

import CogsDefinition from '@/components/widgets/CogsDefinition.vue';
import OrganizationPeerGroupPackets from '@/components/OrganizationPeerGroupPackets.vue';
import PeerGroupMemberList from '@/components/PeerGroupMemberList.vue';
import PeerGroupMemberListArchived from '@/components/PeerGroupMemberListArchived.vue';
import PersistableItem from '@/components/PersistableItem.vue';

export default Vue.extend({
  components: {
    CogsDefinition,
    OrganizationPeerGroupPackets,
    PeerGroupMemberList,
    PeerGroupMemberListArchived,
    PersistableItem
  },

  props: {
    peerGroupId: Number
  },

  data() {
    return {
      isMemberDialogOpen: false
    };
  },

  computed: {
    organizationId(): number {
      return store.state.org.basic.organizationId;
    },

    peerGroup(): OrgPeerGroup {
      return store.getters.org.peerGroup(this.peerGroupId);
    },

    members(): [PeerGroupMember, WebRequestStatus][] {
      return this.peerGroup.members
        .slice()
        .sort(Utils.sortUsers)
        .map(m => [
          m,
          webRequestUtils.leastProgressed([
            store.getters.org.status.relationship(m.userId, this.peerGroupId),
            store.getters.org.status.user(m.userId)
          ])
        ]);
    },

    orgUsers(): PeerGroupMember[] {
      return store.state.org.org.users.map(u => ({
        ...u,
        role: MemberRole.STUDENT,
        manager: UNRULR_MANAGER_ID
      }));
    },

    nonMemberUsers(): [PeerGroupMember, WebRequestStatus][] {
      const activeIds = this.members.reduce(
        (agg, [m]) => agg.add(m.userId),
        new Set<number>()
      );

      return this.orgUsers
        .filter(ou => !activeIds.has(ou.userId))
        .sort(Utils.sortUsers)
        .map(u => [
          u,
          webRequestUtils.leastProgressed([
            store.getters.org.status.relationship(u.userId, this.peerGroupId),
            store.getters.org.status.user(u.userId)
          ])
        ]);
    },

    orgAdmin(): boolean {
      return store.state.org.orgAdmin;
    },

    adminAccess(): boolean {
      return store.state.org.adminAccess;
    },

    isTeacher(): boolean {
      const userId = store.state.org.userInfo.userId;

      return this.peerGroup.members
        .filter(m => m.role === MemberRole.TEACHER)
        .some(m => m.userId === userId);
    },

    canSeeCharts(): boolean {
      return this.isTeacher || this.orgAdmin;
    },

    canEditMembers(): boolean {
      return this.peerGroup.isActive;
    },

    showEditPackets(): boolean {
      return this.peerGroup.isActive;
    },

    status(): WebRequestStatus {
      return store.state.org.activeRequests.group.getStatus(
        this.peerGroup.peerGroupId
      );
    },

    connectCogsAction(): string {
      return '"+"';
    }
  },

  methods: {
    goToSettings() {
      const orgId = this.$router.currentRoute.params['organizationId'];

      this.$router.push({
        name: 'OrganizationPeerGroupSettings',
        params: {
          peerGroupId: `${this.peerGroupId}`,
          organizationId: orgId
        }
      });
    },

    changeRole(member: PeerGroupMember) {
      store.dispatch.org.updateUserGroupConnection({
        userId: member.userId,
        peerGroupId: this.peerGroup.peerGroupId,
        role: member.role
      });
    },

    async addMember(member: PeerGroupMember) {
      store.dispatch.org.addUserGroupConnection({
        userId: member.userId,
        peerGroupId: this.peerGroup.peerGroupId,
        role: member.role
      });
    },

    async addMembers(members: PeerGroupMember[]) {
      for (const member of members) {
        await this.addMember(member);
      }
    },

    removeMember(member: PeerGroupMember) {
      store.dispatch.org.removeUserGroupConnection({
        userId: member.userId,
        peerGroupId: this.peerGroup.peerGroupId,
        role: member.role
      });
    },

    async createMember(email: string, role?: MemberRole) {
      if (!Utils.validateEmail(email)) {
        return;
      }

      store.dispatch.org.createUserForGroup({
        peerGroupId: this.peerGroup.peerGroupId,
        email,
        role
      });
    },

    createMemberWithRole({ email, role }: { email: string; role: MemberRole }) {
      this.createMember(email, role);
    },

    async createMembersWithRole({
      emails,
      role
    }: {
      emails: string[];
      role: MemberRole;
    }) {
      for (const email of emails) {
        await this.createMember(email, role);
      }
    },

    openAddMember() {
      this.isMemberDialogOpen = true;
    },

    closeAddMember() {
      this.isMemberDialogOpen = false;
    },

    openAddPacket() {
      this.$router.push({
        name: 'OrganizationPeerGroupGoals',
        params: {
          organizationId: `${this.organizationId}`,
          peerGroupId: `${this.peerGroupId}`
        }
      });
    },

    reactivatePeerGroup() {
      store.dispatch.org.reactivatePeerGroup(this.peerGroup.peerGroupId);
    }
  }
});
